/* eslint-disable no-debugger */
import React, { useState, useEffect, createContext, useContext } from "react";
import { isEqual } from "lodash";

interface QueryMatch {
    [key: string]: boolean;
    xs: boolean;
    sm: boolean;
    md: boolean;
    lg: boolean;
    xl: boolean;
    xxl: boolean;
    xxxl: boolean;
    portrait: boolean;
}

const defaultValue: QueryMatch = {
    xs: false,
    sm: false,
    md: false,
    lg: false,
    xl: false,
    xxl: false,
    xxxl: false,
    portrait: false,
};

const defaultJsQueries: any = {
    xs: "(max-width: 320px)",
    sm: "(min-width: 321px) and (max-width: 720px)",
    md: "(min-width: 721px) and (max-width: 1024px)",
    lg: "(min-width: 1025px) and (max-width: 1280px)",
    xl: "(min-width: 1281px) and (max-width: 1920px)",
    xxl: "(min-width: 1921px) and (max-width: 2560px)",
    xxxl: "(min-width: 2561px)",
    portrait: "(orientation: portrait)", // we can check orientation also
};

const BreakpointContext = createContext(defaultValue);

const BreakpointProvider = ({ children }: { children: any }) => {
    const queries = defaultJsQueries;
    const [queryMatch, setQueryMatch] = useState<QueryMatch>(defaultValue);

    useEffect(() => {
        const mediaQueryLists: { [key: string]: any } = {};
        const keys = Object.keys(queries);
        let isAttached = false;

        const handleQueryListener = () => {
            const updatedMatches = keys.reduce((acc: any, media: any) => {
                acc[media] = !!(mediaQueryLists[media] && mediaQueryLists[media].matches);
                return acc;
            }, {});
            setQueryMatch(updatedMatches);
        };

        if (window && window.matchMedia) {
            const matches: QueryMatch = { ...defaultValue };
            keys.forEach(media => {
                if (typeof queries[media] === "string") {
                    mediaQueryLists[media] = window.matchMedia(queries[media]);
                    matches[media] = mediaQueryLists[media].matches;
                } else {
                    matches[media] = false;
                }
            });
            setQueryMatch(matches);
            isAttached = true;
            keys.forEach(media => {
                if (typeof queries[media] === "string") {
                    mediaQueryLists[media].addListener(handleQueryListener);
                }
            });
        }

        return () => {
            if (isAttached) {
                keys.forEach(media => {
                    if (typeof queries[media] === "string") {
                        mediaQueryLists[media].removeListener(handleQueryListener);
                    }
                });
            }
        };
    }, [queries]);

    return (
        <BreakpointContext.Provider value={queryMatch}>
            {isEqual(queryMatch, defaultValue) ? null : children}
        </BreakpointContext.Provider>
    );
};

function useBreakpoint() {
    return useContext(BreakpointContext);
}

export { useBreakpoint, BreakpointProvider };
